import { useCallback, useEffect, useState } from 'react';
import type { UserData } from '../types/user';

const API_URL = '/api/users';

function authHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
}

interface PaginatedResponse {
    data: UserData[];
    current_page: number;
    last_page: number;
    total: number;
}

interface UseUsersApiResult {
    users: UserData[];
    loading: boolean;
    error: string | null;
    validationErrors: Record<string, string[]> | null;
    total: number;
    refetch: () => void;
    createUser: (data: Record<string, unknown>) => Promise<UserData>;
    updateUser: (id: number, data: Record<string, unknown>) => Promise<UserData>;
    deleteUser: (id: number) => Promise<void>;
}

export function useUsersApi(): UseUsersApiResult {
    const [users, setUsers] = useState<UserData[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]> | null>(null);
    const [total, setTotal] = useState(0);
    const [trigger, setTrigger] = useState(0);

    const refetch = useCallback(() => setTrigger((t) => t + 1), []);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(null);

        fetch(API_URL, { headers: authHeaders(), signal: controller.signal })
            .then((res) => {
                if (res.status === 403) throw new Error('Accès réservé aux administrateurs.');
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res.json();
            })
            .then((json: PaginatedResponse) => {
                setUsers(json.data);
                setTotal(json.total);
                setLoading(false);
            })
            .catch((err) => {
                if (err.name !== 'AbortError') {
                    setError(err.message);
                    setLoading(false);
                }
            });

        return () => controller.abort();
    }, [trigger]);

    const createUser = useCallback(async (data: Record<string, unknown>): Promise<UserData> => {
        setValidationErrors(null);
        const res = await fetch(API_URL, {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify(data),
        });
        if (res.status === 422) {
            const body = await res.json();
            setValidationErrors(body.errors);
            throw new Error('Erreurs de validation');
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const user = await res.json();
        refetch();
        return user;
    }, [refetch]);

    const updateUser = useCallback(async (id: number, data: Record<string, unknown>): Promise<UserData> => {
        setValidationErrors(null);
        const res = await fetch(`${API_URL}/${id}`, {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify(data),
        });
        if (res.status === 422) {
            const body = await res.json();
            setValidationErrors(body.errors);
            throw new Error('Erreurs de validation');
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const user = await res.json();
        refetch();
        return user;
    }, [refetch]);

    const deleteUser = useCallback(async (id: number): Promise<void> => {
        const res = await fetch(`${API_URL}/${id}`, {
            method: 'DELETE',
            headers: authHeaders(),
        });
        if (res.status === 422) {
            const body = await res.json();
            throw new Error(body.message);
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        refetch();
    }, [refetch]);

    return { users, loading, error, validationErrors, total, refetch, createUser, updateUser, deleteUser };
}
